import { useState } from "react";
import { UseFieldArrayReplace, UseFormSetValue } from "react-hook-form";
import { useTarifsFixes } from "./use-tarifs-fixes";
import { CourseForm } from "../types/forms/course-form";
import { InfoTransportArretForm } from "../types/forms/tarif-fixe-course-form";
import { createEmptyArret } from "../utils/course-stops";

type UseTarifFixeSelectionParams = {
  /** Voir {@link useTarifsFixes} : le back-office cible une entreprise, le client la sienne. */
  isAdmin: boolean;
  idEntreprise?: number | string | null;
  /** Faux quand aucun tarif n'est réservable (édition, visiteur anonyme). */
  actif?: boolean;
  setValue: UseFormSetValue<CourseForm>;
  replace: UseFieldArrayReplace<CourseForm, "arrets">;
};

/**
 * Sélection d'un tarif négocié dans le formulaire de course : le tarif choisi,
 * les informations de vol/train saisies pour ses arrêts, et la répercussion du
 * tarif sur le formulaire (service, retour, itinéraire).
 *
 * Partagé par le formulaire client et celui du back-office : les deux doivent
 * appliquer le tarif exactement de la même façon, sans quoi les deux parcours
 * n'envoient pas la même course au backend.
 */
export const useTarifFixeSelection = ({
  isAdmin,
  idEntreprise,
  actif = true,
  setValue,
  replace,
}: UseTarifFixeSelectionParams) => {
  const [idTarifFixe, setIdTarifFixe] = useState<number | null>(null);
  const [infosTransport, setInfosTransport] = useState<
    InfoTransportArretForm[]
  >([]);

  const { tarifsFixes, isLoading } = useTarifsFixes({
    isAdmin,
    idEntreprise,
    actif,
  });

  const tarifFixe =
    tarifsFixes.find((tarif) => tarif.id === idTarifFixe) ?? null;

  const handleTarifFixeChange = (id: number | null) => {
    setIdTarifFixe(id);
    setInfosTransport([]);

    const tarif = tarifsFixes.find((t) => t.id === id);

    if (tarif) {
      // Le service fait partie du tarif convenu : le reporter dans le formulaire évite d'en
      // redemander un à l'étape suivante, qui l'exige.
      setValue("idService", tarif.idService);

      // L'itinéraire figé par le tarif ne prévoit pas de retour : le champ reste verrouillé
      // (cf. StopItem) tant qu'un tarif est sélectionné, sa valeur doit donc rester à false.
      setValue("avecTrajetRetour", false);
      setValue("dateTrajetRetour", null);
    }

    // Les champs d'adresse ne sont plus exigés (cf. getFieldsToValidate) mais le trajet du
    // tarif doit malgré tout remplir `arrets` : c'est de là que le récapitulatif final tire
    // les adresses à afficher.
    replace(
      tarif
        ? [...tarif.arrets]
            .sort((a, b) => a.ordre - b.ordre)
            .map((arret) => ({
              ordre: arret.ordre,
              type: arret.type,
              adresse: arret.adresse,
              companyTransport: null,
              numeroTransport: null,
            }))
        : [createEmptyArret(0), createEmptyArret(1)],
    );
  };

  return {
    tarifsFixes,
    isLoading,
    tarifFixe,
    handleTarifFixeChange,
    infosTransport,
    setInfosTransport,
  };
};
